import React, { useState, useEffect } from 'react';
import { supabaseFetch } from '../../lib/supabase';
import { useTranslation } from '../../i18n/useTranslation';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import UnifiedLocationSelector from './UnifiedLocationSelector';
import Modal from '../core/Modal';
import { DEFAULT_COUNTRY_CODE, getCountryPresentationDefaults } from '../../data/countriesService';

/**
 * MapLocationPicker — map picker for guest browse, registration gate, and
 * on-demand home-área change.
 *
 * Three layouts:
 *   browse   — dropdown panel anchored below the header locality text,
 *              light backdrop, click-outside closes.
 *   required — centred blocking modal, dark backdrop, non-dismissable.
 *   change   — centred dismissable modal via the shared `core/Modal` shell
 *              (LAYOUT.md § Modal Shell) — ESC/backdrop-click/focus-trap for
 *              free. `browse`/`required` remain on their pre-existing bespoke
 *              chrome (a documented shell exception); `change` is new, so it
 *              is not repeating that debt.
 *
 * Opening view is country-agnostic: reads group/leaf levels from
 * location_level_config via get_available_levels, then calls the
 * proximity RPC which itself reads per-country config. For `change`,
 * `detectedBoundaryId` is the user's *current* home boundary (if any), so
 * the map opens near their existing home rather than an IP-detected one.
 *
 * Props:
 *   mode              — 'browse' (dismissable dropdown) | 'required' (blocking modal) |
 *                        'change' (dismissable centred modal)
 *   onSelect          — single-select: ({ boundaryId, name, countryCode, lat, lon }) => void
 *   onClose           — () => void
 *   detectedBoundaryId — guest's IP-detected boundary ID, or (mode="change") the current home —
 *                        also drives the opening-view framing when multiSelect is used, so
 *                        passing a just-picked home here frames the map on it with its
 *                        surrounding parishes filling the view.
 *   countryCode       — 'PT' | 'GB' etc.
 *   multiSelect       — false (default, single-select via onSelect) | true (multi-select via
 *                       onBoundaryChange, e.g. the registration wizard's "build your área" stage)
 *   initialBoundaries — multiSelect only: boundary ids pre-selected on open
 *   onBoundaryChange  — multiSelect only: (selectedBoundaryIds, boundaries) => void
 *   embedded          — false (default, this component's own modal/dropdown/backdrop chrome) |
 *                       true (no chrome at all — just the resolved map/selector, so a caller
 *                       that already owns its own panel — e.g. a wizard step — supplies title,
 *                       prompt, and surrounding container instead of nesting a second modal)
 *   isHomeAreaBuilding — 2026-08-26 crosshair tuning: multiSelect only. Opts into the
 *                       registration wizard's home-área choreography (see
 *                       UnifiedLocationSelector's own prop doc) and, when set, forwards
 *                       detectedBoundaryId through as UnifiedLocationSelector's
 *                       `seedBoundaryId` — the enriched opening's pre-select source.
 *   onTargetSeeded    — pass-through to UnifiedLocationSelector — (boundary) => void
 *
 *   copyVariant       — pass-through to UnifiedLocationSelector (2026-08-26 save-and-exit/parity
 *                       build) — 'home' (default) | 'newLocation' (MyLocations).
 *   preserveView      — { center: [lat,lng], zoom } (Part 3 P3). When given, skips the entire
 *                       async opening-view resolution below (no RPC round-trip needed — the exact
 *                       view is already known) and passes it straight through to
 *                       UnifiedLocationSelector as initialCenter/initialZoom, so a caller
 *                       transitioning between two of its own stages (the wizard's home->área move)
 *                       can hold the zoom the user actually chose rather than this component
 *                       reframing to its own freshly-resolved bounding box.
 *   pickerConfirmLabel, onSaveAndExit, saveAndExitLabel, frameFirstTierAdjacency,
 *   growPromptLabel, showCountrySelect — pass-through to UnifiedLocationSelector, see that
 *                       component's own prop doc (the last three: #88 addendum 3).
 *
 * disableInertia is forwarded to UnifiedLocationSelector as `embedded` itself
 * (2026-08-26 panBy crash fix) — an embedded map is always mounted inside a
 * caller-owned modal/wizard panel, where a container width/layout change can
 * land in the same commit as the map's mount; Leaflet's own drag-inertia
 * queues a deferred `panBy` after release that can then fire against a
 * container mid-reflow. MyLocations mounts UnifiedLocationSelector directly
 * (not through this component), so it is unaffected by this flag.
 */
const MapLocationPicker = ({
  mode = 'browse',
  onSelect,
  onClose,
  detectedBoundaryId = null,
  countryCode = DEFAULT_COUNTRY_CODE,
  multiSelect = false,
  initialBoundaries = [],
  onBoundaryChange = null,
  embedded = false,
  isHomeAreaBuilding = false,
  copyVariant = 'home',
  onTargetSeeded = null,
  preserveView = null,
  pickerConfirmLabel = null,
  onSaveAndExit = null,
  saveAndExitLabel = null,
  frameFirstTierAdjacency = false,
  growPromptLabel = null,
  showCountrySelect = true
}) => {
  const { t } = useTranslation('location');
  const { isMobile } = useMediaQuery();
  const isDismissable = mode === 'browse';
  const isDropdown = mode === 'browse';

  // Opening-view state: resolved from detectedBoundaryId
  const [openingLevel, setOpeningLevel] = useState(null);
  const [openingParentId, setOpeningParentId] = useState(null);
  const [openingBounds, setOpeningBounds] = useState(null);
  const [openingProximityLabel, setOpeningProximityLabel] = useState(null);
  const [resolving, setResolving] = useState(!preserveView);
  // Never updated after mount (preserveView is a one-shot handoff from the caller, not something
  // that changes mid-life like openingBounds does via the async resolution below). Captured once:
  // a caller passing an inline literal hands a new array every render, and the selector re-frames
  // the map whenever initialCenter changes identity — that was the re-zoom on every parish
  // selected (Roadmap #88 addendum 3, A1).
  const [preservedView] = useState(() => (preserveView
    ? { center: preserveView.center || null, zoom: preserveView.zoom ?? null }
    : null));
  const openingCenter = preservedView?.center || null;
  const openingZoom = preservedView?.zoom ?? null;

  // Resolve opening view on mount — skipped entirely when preserveView is given (Part 3 P3): the
  // exact view is already known, so no RPC round-trip (or its own bounding-box reframing) is
  // needed at all.
  useEffect(() => {
    if (preservedView) return;
    let cancelled = false;
    (async () => {
      try {
        if (detectedBoundaryId) {
          // Resolve the detected boundary to determine its level and parent
          const { data: boundaries } = await supabaseFetch.rpc('get_boundaries_by_ids', {
            p_ids: [detectedBoundaryId]
          });
          const boundary = boundaries?.[0];

          if (boundary) {
            const detectedCountry = boundary.country_code?.trim() || countryCode;

            // Load level config for this country to find group/leaf levels
            const { data: levelConfig } = await supabaseFetch.rpc('get_available_levels', {
              p_country_code: detectedCountry
            });

            // Find the group level (lowest is_user_selectable level = group/opening level)
            // and leaf level (per proximity_config, or lowest selectable as fallback)
            const selectableLevels = (levelConfig || [])
              .filter(l => l.is_user_selectable)
              .sort((a, b) => a.admin_level - b.admin_level);

            const groupLevel = selectableLevels.length > 0 ? selectableLevels[0].admin_level : null;
            const leafLevel = selectableLevels.length > 1
              ? selectableLevels[selectableLevels.length - 1].admin_level
              : groupLevel;

            if (!groupLevel) {
              if (!cancelled) fallbackToRegionView(detectedCountry);
              return;
            }

            // Walk up from the detected boundary to find the group-level anchor
            let groupAnchorId = null;
            if (boundary.admin_level === groupLevel) {
              groupAnchorId = boundary.id;
            } else if (boundary.admin_level > groupLevel || boundary.parent_id) {
              // Detected boundary is below group level — walk up via parent_id
              // For most cases one hop is enough (ward→LAD, freguesia→concelho)
              let currentId = boundary.parent_id;
              let currentLevel = null;
              // Fetch parent to check its level
              if (currentId) {
                const { data: parentBoundaries } = await supabaseFetch.rpc('get_boundaries_by_ids', {
                  p_ids: [currentId]
                });
                const parent = parentBoundaries?.[0];
                if (parent) {
                  currentLevel = parent.admin_level;
                  if (currentLevel === groupLevel) {
                    groupAnchorId = currentId;
                  } else if (currentLevel > groupLevel && parent.parent_id) {
                    // Need another hop (e.g. if detection returned a sub-ward level)
                    groupAnchorId = parent.parent_id;
                  }
                }
              }
            }

            if (groupAnchorId) {
              // Try proximity opening view (works for any country with proximity_config)
              const proximityResult = await resolveProximityView(groupAnchorId, detectedCountry, leafLevel, cancelled);
              if (!proximityResult && !cancelled) {
                await resolveSingleGroupView(groupAnchorId, leafLevel, cancelled);
              }
            } else {
              if (!cancelled) fallbackToRegionView(detectedCountry);
            }
          } else {
            if (!cancelled) fallbackToRegionView(countryCode);
          }
        } else {
          if (!cancelled) fallbackToRegionView(countryCode);
        }
      } catch (err) {
        console.error('MapLocationPicker: opening view resolution failed:', err);
        if (!cancelled) fallbackToRegionView(countryCode);
      } finally {
        if (!cancelled) setResolving(false);
      }
    })();

    // Country-agnostic proximity: call get_freguesias_near_boundary (reads config per country)
    async function resolveProximityView(anchorGroupId, country, leafLevel, isCancelled) {
      try {
        const { data: nearby, error } = await supabaseFetch.rpc('get_freguesias_near_boundary', {
          p_boundary_id: anchorGroupId,
          p_include_geojson: false
        });

        if (error || !nearby || nearby.length === 0) {
          console.warn('MapLocationPicker: proximity RPC returned empty or error, falling back');
          return false;
        }

        // Extract distinct parent IDs (for multi-group case like PT)
        // or the boundary IDs themselves (for same-level case like GB)
        const parentIds = [...new Set(nearby.map(f => f.parent_id).filter(Boolean))];
        const resultIds = [...new Set(nearby.map(f => f.id))];

        // Get bbox for the anchor group boundary
        const { data: centroid } = await supabaseFetch.rpc('get_boundary_centroid', {
          p_boundary_ids: [anchorGroupId]
        });

        if (!isCancelled) {
          if (centroid?.[0]?.min_lat != null) {
            const c = centroid[0];
            const latPad = (c.max_lat - c.min_lat) * 0.5;
            const lngPad = (c.max_lng - c.min_lng) * 0.5;
            setOpeningBounds([
              [c.min_lat - latPad, c.min_lng - lngPad],
              [c.max_lat + latPad, c.max_lng + lngPad]
            ]);
          }

          // Determine the opening level and parent filter based on result shape
          const resultLevel = nearby[0]?.admin_level;
          setOpeningLevel(resultLevel || leafLevel);

          // If results have parents different from themselves, use parent filter
          // (PT: results are freguesias, parentIds are concelhos)
          // If results ARE the group level (GB: LADs returned directly), no parent filter needed
          if (resultLevel === leafLevel && parentIds.length > 0 && parentIds[0] !== resultIds[0]) {
            setOpeningParentId(parentIds);
          } else {
            // Same-level case: pass the result IDs as a pre-loaded set
            // ULS will load these boundaries by level without parent filter
            setOpeningParentId(null);
          }

          setOpeningProximityLabel(t('picker_near_you'));
          console.log(`✅ Proximity opening view (${country}): ${nearby.length} boundaries`);
        }
        return true;
      } catch (err) {
        console.warn('MapLocationPicker: proximity RPC failed:', err);
        return false;
      }
    }

    // Single-group fallback (shows leaf children of anchor group)
    async function resolveSingleGroupView(groupId, leafLevel, isCancelled) {
      const { data: centroid } = await supabaseFetch.rpc('get_boundary_centroid', {
        p_boundary_ids: [groupId]
      });
      if (centroid?.[0]?.min_lat != null && !isCancelled) {
        const c = centroid[0];
        setOpeningBounds([[c.min_lat, c.min_lng], [c.max_lat, c.max_lng]]);
      }
      if (!isCancelled) {
        setOpeningLevel(leafLevel);
        setOpeningParentId(groupId);
      }
    }

    function fallbackToRegionView(country) {
      const defaults = getCountryPresentationDefaults(country);
      // Load the group level for this country (first selectable level)
      // Use a synchronous fallback since we can't await here
      setOpeningLevel(null); // ULS will load from config
      setOpeningParentId(null);
      setOpeningBounds(defaults.bounds);
    }

    return () => { cancelled = true; };
  }, [detectedBoundaryId, countryCode, t, preservedView]);

  // 2026-09-02 unit-label-per-country + selector reorder build: was two standalone divs here
  // (a MapLocationPicker-level sibling ABOVE mapContent, i.e. above the country-select nested
  // inside it — the exact "instruction above Country" ordering the brief's screenshot flagged),
  // one hardcoding "freguesia" in English. Now passed into UnifiedLocationSelector itself, which
  // renders it right after its own country-select (Country first, then instruction). Gated on
  // !embedded — RegisterLocationStep's embedded home-stage (mode="change" embedded) already
  // renders its own separate external prompt outside mapContent; without this gate it would get
  // a duplicate internal banner. "browse" mode never showed a prompt and still doesn't
  // (instructionTemplateKey stays null for it).
  const instructionTemplateKey = embedded ? null
    : mode === 'change' ? 'picker_change_prompt'
    : mode === 'required' ? 'picker_required_prompt'
    : null;
  const instructionTemplateFallback = embedded ? null
    : mode === 'change' ? 'Pick a {unit} to make it your new home. Your saved area stays as it is.'
    : mode === 'required' ? 'Please choose your area to see local news.'
    : null;

  // ── Shared map content ──
  const mapContent = multiSelect ? (
    <UnifiedLocationSelector
      mode="user"
      allowBoundary={true}
      allowPin={false}
      multiSelect={true}
      initialBoundaries={initialBoundaries}
      initialCountry={countryCode}
      onBoundaryChange={onBoundaryChange}
      initialBounds={openingBounds}
      initialCenter={openingCenter}
      initialZoom={openingZoom}
      initialLevel={openingLevel}
      initialParentId={openingParentId}
      proximityLabel={openingProximityLabel}
      compact={true}
      isHomeAreaBuilding={isHomeAreaBuilding}
      copyVariant={copyVariant}
      seedBoundaryId={isHomeAreaBuilding ? detectedBoundaryId : null}
      onTargetSeeded={onTargetSeeded}
      disableInertia={embedded}
      onSaveAndExit={onSaveAndExit}
      saveAndExitLabel={saveAndExitLabel}
      frameFirstTierAdjacency={frameFirstTierAdjacency}
      growPromptLabel={growPromptLabel}
      showCountrySelect={showCountrySelect}
      instructionTemplateKey={instructionTemplateKey}
      instructionTemplateFallback={instructionTemplateFallback}
    />
  ) : (
    <UnifiedLocationSelector
      mode="picker"
      allowBoundary={true}
      allowPin={false}
      multiSelect={false}
      initialCountry={countryCode}
      onSelect={onSelect}
      initialBounds={openingBounds}
      initialCenter={openingCenter}
      initialZoom={openingZoom}
      initialLevel={openingLevel}
      initialParentId={openingParentId}
      proximityLabel={openingProximityLabel}
      compact={true}
      disableInertia={embedded}
      pickerConfirmLabel={pickerConfirmLabel}
      instructionTemplateKey={instructionTemplateKey}
      instructionTemplateFallback={instructionTemplateFallback}
    />
  );

  // ── Embedded mode: no chrome — caller supplies title/prompt/container (e.g. a wizard step) ──
  if (embedded) {
    if (resolving) {
      return (
        <div style={{ padding: '24px', fontSize: '14px', color: '#6B7280', fontFamily: 'OpenSauceSans, sans-serif' }}>
          {t('picker_loading')}
        </div>
      );
    }
    return mapContent;
  }

  // ── Loading state ──
  if (resolving) {
    if (isDropdown) {
      // Dropdown: inline loading pill, no backdrop
      return (
        <>
          <div
            onClick={onClose}
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10001, pointerEvents: 'auto' }}
          />
          <div style={{
            position: 'fixed',
            top: '55px',
            left: isMobile ? '50%' : 'calc(50% + 180px)',
            transform: 'translateX(-50%)',
            zIndex: 10002,
            pointerEvents: 'auto',
            padding: '12px 24px',
            background: 'white',
            borderRadius: '8px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            fontSize: '13px',
            color: '#6B7280',
            fontFamily: 'OpenSauceSans, sans-serif'
          }}>
            {t('picker_loading')}
          </div>
        </>
      );
    }
    if (mode === 'change') {
      // Dismissable modal: loading through the shared shell too, so ESC/backdrop-click work
      // even before the map content resolves.
      return (
        <Modal isOpen={true} onClose={onClose} panelStyle={{ padding: '24px', background: 'white', borderRadius: '12px' }}>
          <div style={{ fontSize: '14px', color: '#6B7280', fontFamily: 'OpenSauceSans, sans-serif' }}>
            {t('picker_loading')}
          </div>
        </Modal>
      );
    }
    // Modal: centred loading (required mode — bespoke, non-dismissable)
    return (
      <div style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 10001, pointerEvents: 'auto'
      }}>
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.4)'
        }} />
        <div style={{
          position: 'relative', padding: '24px', background: 'white',
          borderRadius: '12px', zIndex: 10002, fontSize: '14px', color: '#6B7280'
        }}>
          {t('picker_loading')}
        </div>
      </div>
    );
  }

  // ── BROWSE mode: dropdown panel below header ──
  if (isDropdown) {
    return (
      <>
        {/* Click-outside backdrop — transparent */}
        <div
          onClick={onClose}
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            zIndex: 10001,
            pointerEvents: 'auto'
          }}
        />

        {/* Dropdown card — desktop: centred on content column (180px right of viewport centre) */}
        <div style={{
          position: 'fixed',
          top: '55px',
          left: isMobile ? '50%' : 'calc(50% + 180px)',
          transform: 'translateX(-50%)',
          width: isMobile ? 'calc(100% - 24px)' : '500px',
          maxHeight: isMobile ? 'calc(100vh - 70px)' : '80vh',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 6px 24px rgba(0,0,0,0.15)',
          zIndex: 10002,
          pointerEvents: 'auto',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          fontFamily: 'OpenSauceSans, sans-serif'
        }}>
          {/* Compact header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 14px 8px',
            borderBottom: '1px solid #E5E7EB'
          }}>
            <span style={{
              fontSize: '14px',
              fontWeight: '700',
              color: '#024B99'
            }}>
              {t('picker_title')}
            </span>
            <button
              onClick={onClose}
              style={{
                background: 'none', border: 'none',
                fontSize: '18px', color: '#9CA3AF',
                cursor: 'pointer', padding: '0 2px', lineHeight: 1
              }}
            >
              &times;
            </button>
          </div>

          {/* Map — explicit height so Leaflet gets real dimensions */}
          <div style={{
            flex: 1,
            overflow: 'auto',
            padding: '8px'
          }}>
            {mapContent}
          </div>
        </div>
      </>
    );
  }

  // ── CHANGE mode: dismissable centred modal, via the shared shell ──
  if (mode === 'change') {
    return (
      <Modal
        isOpen={true}
        onClose={onClose}
        panelStyle={{
          width: '100%',
          maxWidth: '600px',
          maxHeight: '90vh',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          margin: '16px'
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px 16px 10px',
          borderBottom: '1px solid #E5E7EB'
        }}>
          <h3 style={{
            margin: 0,
            fontSize: '16px',
            fontWeight: '700',
            color: '#024B99',
            fontFamily: 'OpenSauceSans, sans-serif'
          }}>
            {t('picker_title_change')}
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none',
              fontSize: '18px', color: '#9CA3AF',
              cursor: 'pointer', padding: '0 2px', lineHeight: 1
            }}
          >
            &times;
          </button>
        </div>

        {/* Change mode prompt — 2026-09-02 build: now rendered inside mapContent itself, after
            the country-select (Country first, then instruction). See instructionTemplateKey
            above. */}
        {/* Map picker */}
        <div style={{ flex: 1, overflow: 'auto', padding: '12px' }}>
          {mapContent}
        </div>
      </Modal>
    );
  }

  // ── REQUIRED mode: centred blocking modal ──
  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 10001, pointerEvents: 'auto'
    }}>
      {/* Dark backdrop — not clickable */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.4)'
      }} />

      {/* Modal card */}
      <div style={{
        position: 'relative',
        width: '100%',
        maxWidth: '600px',
        maxHeight: '90vh',
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
        zIndex: 10002,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        margin: '16px'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px 16px 10px',
          borderBottom: '1px solid #E5E7EB'
        }}>
          <h3 style={{
            margin: 0,
            fontSize: '16px',
            fontWeight: '700',
            color: '#024B99',
            fontFamily: 'OpenSauceSans, sans-serif'
          }}>
            {t('picker_title_required')}
          </h3>
        </div>

        {/* Required mode prompt — 2026-09-02 build: now rendered inside mapContent itself, after
            the country-select. See instructionTemplateKey above. */}
        {/* Map picker */}
        <div style={{ flex: 1, overflow: 'auto', padding: '12px' }}>
          {mapContent}
        </div>
      </div>
    </div>
  );
};

export default MapLocationPicker;
